/*!
 * Copyright 2019 Fondazione Bruno Kessler
 * Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
 * Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
 * except in compliance with the Licence.
 *
 * You may obtain a copy of the Licence at:
 * http://ec.europa.eu/idabc/eupl.html
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the Licence for the specific language
 * governing permissions and limitations under the Licence.
 */

import {KeyValue} from "@angular/common";
// eslint-disable-next-line max-len
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  LOCALE_ID,
  NgZone,
  OnDestroy,
  OnInit,
  TrackByFunction,
  ViewChild,
} from "@angular/core";
import {
  AbstractControl,
  FormGroupDirective,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from "@angular/forms";
import {RegistryContexts} from "@app/models/constants/registry-constants";
import {DATE_FMT} from "@app/models/date-fmt";
import {generateUuid} from "@app/models/functions/generate-uuid.function";
import {CommonState} from "@app/store/common/common.state";
import {ONCO_DRUGS_IDS} from "@doctor-panel-modules/patient-treatment-plan/models/onco-drugs";
import {TherapyCycleInfo} from "@doctor-panel-modules/patient-treatment-plan/models/therapy-cycle-info";
import {environment} from "@env/environment";
import {InputCustomEvent, ModalController} from "@ionic/angular";
import {Store} from "@ngxs/store";
// eslint-disable-next-line max-len
import {
  ClinicalDataService,
  ClinicalDataType,
  TrecAssumption,
  TrecAssumptionUnit,
  TrecFreqType,
  TrecTherapy,
  TrecTherapyAssumptionData,
  TrecVariableAssumption,
} from "@trec/api/clinical-data";
import {Drug, DrugStoreService, TherapyTemplate} from "@trec/api/drug-store";
import {c3MinDateValidator} from "@trec/components/form-utils";
import {C3SnackbarStatusService} from "@trec/components/snackbar-status";
import {addDays, differenceInDays, endOfDay, format, startOfDay} from "date-fns";
import {BehaviorSubject, Observable, Subject, Subscription, of} from "rxjs";
import {catchError, debounceTime, pairwise, switchMap, tap} from "rxjs/operators";
import {DrugSelectorComponent} from "../../others/drug-selector/drug-selector.component";
import {CdItemFormComponent, CdItemFormMode} from "../cd-item-form-component";

export interface ForTherapyInsertDefaults {
  drug?: Drug;

  assumptionsUnit?: TrecAssumptionUnit;

  regular?: {
    frequency: {
      type: TrecFreqType;
      values: number[];
    };
  };

  /**
   * Whether it's necessary to add at least one assumption or not.
   */
  atLeastOneAssumptionNeeded?: boolean;

  /**
   * The desired duration of therapy in days.
   * Negative values mean the therapy never ends.
   */
  therapyDuration?: number;
}

interface Prescription {
  doctor: string | undefined;
  structure: string | undefined;
  state: string | undefined;
  date: string | undefined;
}

interface FormTherapyDataRegular {
  frequency: {type: TrecFreqType; value: number | number[] | null} | null; // TrecFreqType - (number | number[] | null)
  assumptions: TrecTherapyAssumptionData[] | null;
}

interface FormTherapyData {
  drug: Drug | null;
  assumptionsUnit: TrecAssumptionUnit | null;
  startDate: string | null;
  neverEnds: boolean;
  duration: number | null;
  endDate: string | null; // Date

  regular: FormTherapyDataRegular;

  variable: {
    dailyAssumptions: TrecVariableAssumption[] | null;
  };

  notes: string | null;
}

type FormTherapyDataKeys = {[key in keyof FormTherapyData]: any};

@Component({
  selector: "c3-form-therapy",
  templateUrl: "./form-therapy.component.html",
  styleUrls: ["./form-therapy.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FormTherapyComponent
  extends CdItemFormComponent<TrecTherapy, FormTherapyData>
  implements OnInit, OnDestroy {
  // IO ///////////////////////////////////////////////////////////////////////////////////////////
  @Input() AIFlag!: boolean;
  @Input()
  public get mode(): CdItemFormMode {
    return super.mode;
  }
  public set mode(value: CdItemFormMode) {
    super.mode = value;
  }

  @Input()
  public get item(): TrecTherapy | undefined {
    return super.item;
  }
  public set item(value: TrecTherapy | undefined) {
    super.item = value;
  }

  private _context?: RegistryContexts | undefined | null;
  @Input()
  public get context(): RegistryContexts | undefined | null {
    return this._context;
  }
  public set context(value: RegistryContexts | undefined | null) {
    this._context = value;
    // Update form
    this.updateAssumptionUnits();
    this._update$.next(true);
  }

  private get isDiabetesContext(): boolean | undefined | null {
    if (!this.context) {
      return this.context;
    }

    return [
      RegistryContexts.DIABETES1,
      RegistryContexts.DIABETES2,
      RegistryContexts.GESTATIONAL,
      RegistryContexts.PREPREGNANCY,
    ].includes(this.context);
  }

  private _therapyTemplate: TherapyTemplate | null = null;
  @Input()
  public get therapyTemplate(): TherapyTemplate | null {
    return this._therapyTemplate;
  }
  public set therapyTemplate(value: TherapyTemplate | null) {
    this._therapyTemplate = value;
    // Update form
    this._update$.next(true);
  }

  /**
   * Control handling the drug object / the search query string.
   */
  get drugControl(): UntypedFormControl {
    return this.form.get("drug") as UntypedFormControl;
  }

  /**
   * Regular assumption form array from main form.
   */
  get assumptionsControls(): UntypedFormArray {
    return this.form.get("regular.assumptions") as UntypedFormArray;
  }

  /**
   * Variable daily assumptions in form.
   */
  get variableDailyAssumptionsControls(): UntypedFormArray {
    return this.form.get("variable.dailyAssumptions") as UntypedFormArray;
  }

  get assumptionsUnitControl(): UntypedFormControl {
    return this.form.get("assumptionsUnit") as UntypedFormControl;
  }

  get startDateControl(): UntypedFormControl {
    return this.form.get("startDate") as UntypedFormControl;
  }

  get endDateControl(): UntypedFormControl {
    return this.form.get("endDate") as UntypedFormControl;
  }

  get neverEndsControl(): UntypedFormControl {
    return this.form.get("neverEnds") as UntypedFormControl;
  }

  get durationControl(): UntypedFormControl {
    return this.form.get("duration") as UntypedFormControl;
  }

  /**
   * Flag indicating to handle only insulin therapies.
   * (Used to filter insulin drugs)
   */
  @Input()
  insulinTherapy!: boolean;

  /**
   * Optional default values to be initially inserted in the form if it is in "insert" mode.
   */
  @Input()
  insertDefaults?: ForTherapyInsertDefaults;

  // Component elems /////////////////////////////////////////////////////////////////////////////

  @ViewChild("formElem", {read: FormGroupDirective, static: true}) formElem!: FormGroupDirective;
  @ViewChild("drugSelector", {static: true}) drugSelector!: DrugSelectorComponent;

  // Drug search //////////////////////////////////////////////////////////////////////////////////

  private readonly _drugSearchLoading$ = new BehaviorSubject<boolean>(false);
  readonly drugSearchLoading$ = this._drugSearchLoading$.asObservable();

  private readonly drugSearchValue$ = new Subject<Drug>();
  readonly drugSearchResults$: Observable<Drug[] | undefined> = this.drugSearchValue$.pipe(
    // Apply a debounce time to avoid storm of search events (-> server has an API limiter)
    debounceTime(300),
    tap(() => {
      this._drugSearchLoading$.next(true);
      this.cd.detectChanges();
    }),
    switchMap((searchDrug) => this.drugStore.searchDrug(searchDrug)), // Search thedrug
    catchError((err) => {
      // TODO handle err
      return of([]);
    }),
    tap(() => {
      this._drugSearchLoading$.next(false);
      this.cd.detectChanges();
    }),
  );

  // Defaults and Enums ///////////////////////////////////////////////////////////////////////////

  private readonly MAX_QUANTITY = 50;
  private readonly DEFAULT_DURATION = 30;
  private readonly DEFAULT_NEVER_ENDS = true; // true: tharapy never ends, false: therapy will complete
  private readonly MAXIMUM_DATE_UNIX = 86300000000000;

  readonly ClinicalDataType = ClinicalDataType;
  ASSUMPTION_UNITS = TrecTherapy.TRANSLATION[this.localeId]["regular.assumptions.unit"];
  readonly DAY_MOMENTS: {[key: string]: string};

  /**
   * Avoid error 'theraphy' cannot start in the past, starting from today.
   */
  readonly today = startOfDay(new Date());
  readonly todayStr = format(this.today, DATE_FMT);

  // Form fields //////////////////////////////////////////////////////////////////////////////////

  readonly form: UntypedFormGroup;

  specialFormType: "generic" | "capecitabine" = "generic";

  /**
   * Toggle visualization of regular or variable therapy.
   */
  therapyType: "regular" | "variable" = "regular";

  /**
   * Information about therapy's assumptions cyclic behavior.
   * If null, therapy has not cyclic assumptions.
   */
  cycleInfo: TherapyCycleInfo | null = null;

  private readonly formValueSubscrs = new Subscription();

  /**
   * Function used to sort entries by value, alphabetically.
   */
  readonly sortAlphabetically = (a: KeyValue<string, string>, b: KeyValue<string, string>) =>
    a.value > b.value ? 1 : -1;
  readonly trackByValue: TrackByFunction<KeyValue<string, string>> = (_i, item) => item.key;

  prescription: Prescription[] = [];

  constructor(
    private fb: UntypedFormBuilder,
    private store: Store,
    private c3SnackbarStatus: C3SnackbarStatusService,
    protected cd: ChangeDetectorRef,
    private modalCtrl: ModalController,
    private zone: NgZone,
    @Inject(LOCALE_ID) private localeId: string,
    private drugStore: DrugStoreService,
    private cdService: ClinicalDataService,
  ) {
    super(cd);
    // Init list of moments (exclude "at_need")
    const {["at_need"]: _, ...moments} = TrecAssumption.TRANSLATION[this.localeId]["moment"];
    this.DAY_MOMENTS = moments;

    // Initialize form schema
    this.form = this.fb.group({
      drug: [null, Validators.required],
      assumptionsUnit: [null, Validators.required],
      startDate: [
        format(this.today, DATE_FMT),
        [Validators.required, c3MinDateValidator(this.today)],
      ], // Date
      neverEnds: [this.DEFAULT_NEVER_ENDS], // therapy will complete / therapy never ends
      duration: [this.DEFAULT_DURATION, Validators.min(1)],
      endDate: [
        this.DEFAULT_NEVER_ENDS
          ? format(new Date(this.MAXIMUM_DATE_UNIX), DATE_FMT)
          : format(addDays(this.today, this.DEFAULT_DURATION), DATE_FMT),
        [
          Validators.required,
          c3MinDateValidator(() => addDays(new Date(this.form?.value.startDate), 1)),
        ],
      ], // Date

      regular: this.fb.group({
        frequency: [{type: "at_need" as TrecFreqType, value: null}, Validators.required], // TrecFreqType - (number | number[] | null)
        assumptions: this.fb.array([]),
      }),

      variable: this.fb.group({
        dailyAssumptions: this.fb.array([]),
      }),

      notes: [""],
    } as FormTherapyDataKeys);
  }

  ngOnInit() {
    // Subscribe for form values changes

    // Drug //////////////////////////////////////////
    this.formValueSubscrs.add(
      this.drugControl.valueChanges.subscribe((drug: Drug | string | null) => {
        if (drug && typeof drug !== "string") {
          // "Validate" data and, potentially, fill missing fields
          if (!drug.box) {
            const updDrug: Drug = {...drug, box: "Non disponibile"};
            this.drugControl.patchValue(updDrug, {emitEvent: false});
          }
        }
      }),
    );

    // Assumption unit ///////////////////////////////
    this.formValueSubscrs.add(
      this.assumptionsUnitControl.valueChanges
        .pipe(pairwise()) // Get also previous value
        .subscribe(([assumptionUnitPrev, assumptionUnitNext]) => {
          if (assumptionUnitNext) {
            if (
              this.assumptionsUnitControl.touched && // User touched the selector
              assumptionUnitPrev && // Assumption unit was already set
              assumptionUnitPrev !== assumptionUnitNext // Assumption unit is different
            ) {
              this.showAssumptionUnitSelectedAlert();
            }

            // Change assumption unit to regular assumptions
            this.assumptionsControls.patchValue(
              (this.formValue.regular?.assumptions ?? []).map((assumption) => ({
                ...assumption,
                unit: assumptionUnitNext,
              })),
            );

            // Change assumption unit to variable assumptions
            this.variableDailyAssumptionsControls.patchValue(
              (this.formValue.variable?.dailyAssumptions ?? []).map((dailyAssumption) => ({
                ...dailyAssumption,
                assumptions:
                  dailyAssumption.assumptions?.map((assumption) => ({
                    ...assumption,
                    unit: assumptionUnitNext,
                  })) ?? [],
              })),
            );
          }
        }),
    );

    // Drug ///////////////////////////////
    this.formValueSubscrs.add(
      this.drugControl.valueChanges.subscribe(() => {
        this.updateSpecialFormType();
      }),
    );

    // Start date ///////////////////////////////
    this.formValueSubscrs.add(
      this.startDateControl.valueChanges.subscribe((startDate: string | null) => {
        // Update cycleInfo padding
        if (this.isEditMode && this.startDateControl.value && this.item) {
          // If therapy has a variable schema -> it must be recalculated removing first n past days
          // (from original start date to the new one)
          const diffDays = differenceInDays(
            new Date(this.startDateControl.value),
            new Date(this.item.metadata.start_date),
          );
          this.cycleInfo = this.cycleInfo
            ? {
              ...this.cycleInfo,
              startPadding: Math.max(
                ((this.item.data.extra?.cycle_view_start_padding as number | undefined) ?? 0) +
                diffDays,
                0,
              ),
            }
            : null;
        }

        const neverEnds = this.formValue.neverEnds;

        if (neverEnds) {
          // There is no duration -> there is no end date
          // Why? https://stackoverflow.com/questions/11526504/minimum-and-maximum-date (a little smaller due to @angular/material issues)
          this.endDateControl.patchValue(format(new Date(this.MAXIMUM_DATE_UNIX), DATE_FMT), {
            emitEvent: false,
          });
        } else {
          if (startDate) {
            const isEditMode = this.mode === "edit";
            if (isEditMode) {
              // In edit mode: keep the end_date set in therapy and do not shift it -> re-patch it to update the duration
              this.endDateControl.patchValue(this.endDateControl.value);
            } else {
              // Re-align end date keeping current set duration
              this.endDateControl.patchValue(
                format(addDays(new Date(startDate), this.formValue.duration ?? 0), DATE_FMT),
                {emitEvent: false},
              );
            }
          }
        }
      }),
    );

    // Duration /////////////////////////////////
    this.formValueSubscrs.add(
      this.durationControl.valueChanges.subscribe((duration: number | null) => {
        this.endDateControl.patchValue(
          format(addDays(new Date(this.startDateControl.value), duration ?? 0), DATE_FMT),
          {emitEvent: false},
        );
      }),
    );

    // End date /////////////////////////////////
    this.formValueSubscrs.add(
      this.endDateControl.valueChanges.subscribe((endDate: string | null) => {
        if (endDate) {
          const startDateControl = this.startDateControl;
          this.durationControl.patchValue(
            differenceInDays(new Date(endDate), new Date(startDateControl.value)),
            {emitEvent: false},
          );
        }
      }),
    );

    // Duration type ////////////////////////////
    this.formValueSubscrs.add(
      this.neverEndsControl.valueChanges.subscribe((neverEnds) => {
        const startDate = this.formValue.startDate;

        if (neverEnds) {
          // There is no duration -> there is no end date
          // Why? https://stackoverflow.com/questions/11526504/minimum-and-maximum-date (a little smaller due to @angular/material issues)
          this.endDateControl.patchValue(format(new Date(this.MAXIMUM_DATE_UNIX), DATE_FMT), {
            emitEvent: false,
          });
          this.durationControl.patchValue(this.DEFAULT_DURATION, {emitEvent: false});
        } else {
          if (startDate) {
            // Calculate duration to apply (if previous "neverEnds" was true, duration would be ~>100 years... but now we do
            // not want to apply this duration, so we use the default one or the one taken from the therapy template if any)
            const duration = this.therapyTemplate?.duration ?? this.DEFAULT_DURATION;
            const newEndDate = format(addDays(new Date(startDate), duration), DATE_FMT);
            this.endDateControl.patchValue(newEndDate, {emitEvent: false});
            this.durationControl.patchValue(duration, {emitEvent: false});
          }
        }
      }),
    );

    const pattern: RegExp = /^[1-9][0-9]*$/;

    // Frequency /////////////////////////////////
    this.formValueSubscrs.add(
      (this.form.get("regular.frequency") as UntypedFormControl).valueChanges
        .pipe(pairwise())
        .subscribe(([prev, frequency]) => {
          // Reset assumptions list when frequency type changes (e.g., click on ion-item in selector)
          try {
            const prevType = prev?.type;
            const newType = frequency?.type;
            if (prevType !== newType) {
              if (this.assumptionsControls && this.assumptionsControls.length > 0 && !this.AIFlag) {
                // Invece di svuotare, aggiorna i valori delle assumptions esistenti
                const currentAssumptions = [...this.assumptionsControls.value];

                if (newType === "at_need") {
                  // Se il nuovo tipo è "at_need", imposta valori di default
                  const newAssumptions = currentAssumptions.map((a) => ({
                    ...a,
                    time: "00:00",
                    moment: "at_need",
                  }));
                  this.assumptionsControls.patchValue(newAssumptions, {emitEvent: false});
                } else if (prevType === "at_need") {
                  // Se si passa da "at_need" ad altro tipo, ripristina valori normali
                  const newAssumptions = currentAssumptions.map((a) => ({
                    ...a,
                    time: a.time === "00:00" ? "08:00" : a.time,
                    moment: a.moment === "at_need" ? undefined : a.moment,
                  }));
                  this.assumptionsControls.patchValue(newAssumptions, {emitEvent: false});
                }

                this.assumptionsControls.markAsDirty();
              }
            }
          } catch (e) {
            // ignore
          }

          // When frequency is "at_need" -> set default values into assumptions (time: 00:00, moment: 'at_need')
          if (frequency && frequency.type === "at_need") {
            if (this.assumptionsControls.length > 0) {
              const newAssumptions = [...this.assumptionsControls.value].map((a) => ({
                ...a,
                time: "00:00",
                moment: "at_need",
              }));
              this.assumptionsControls.patchValue(newAssumptions, {emitEvent: false});
            }
          } else if (
            (frequency.type === "day_of_week" || frequency.type === "day_of_month") &&
            frequency.value.length === 0
          ) {
            (this.form.get("regular.frequency") as UntypedFormControl).setErrors({invalid: true});
          } else if (
            frequency.type === "every_x_days" &&
            (frequency.value < 1 || !pattern.test(frequency.value))
          ) {
            (this.form.get("regular.frequency") as UntypedFormControl).setErrors({invalid: true});
          }
        }),
    );

    // Initialize fields
    this.initFieldsInsertDefaults();

    if (this.item) {
      this.cdService.getStoricoFarmaci(this.item!).subscribe(
        (data: any) => {
          for (let i = 0; i < data.length; i++) {
            let stato;
            switch (data[i].state) {
              case "inserimento":
                stato = "prescritta";
                break;
              case "modifica":
                stato = "modificata";
                break;
              case "cancellazione":
                stato = "cancellata";
                break;
              case "sospensione":
                stato = "sospesa";
                break;
              case "riattivazione":
                stato = "riattivata";
            }
            this.prescription[i] = {
              doctor: data[i].doctor.name,
              structure: data[i].structure,
              date: data[i].date,
              state: stato,
            };
          }
          this.cd.detectChanges();
        },
        (err: any) => {
          console.error(err);
        },
      );
    }
  }

  private toFixedTwoDecimalPlaces(value?: number) {
    if (value === undefined) {
      return;
    }
    const rounded = (Math.round(value * 4) / 4).toFixed(2);
    return Number(rounded);
  }

  onQuantityBlur(_: InputCustomEvent, control: AbstractControl | null) {
    if (control && control.getRawValue().quantity) {
      let quantity = parseFloat(control.getRawValue().quantity)
      if (isNaN(quantity) || quantity < 0) {
        quantity = 0;
      }
      const rounded = this.toFixedTwoDecimalPlaces(quantity);
      const qnt = control.get('quantity');
      if (qnt) {
        qnt.setValue(rounded, {emitEvent: false});
      }
    }
  }

  onQuantityInput(_: InputCustomEvent, control: AbstractControl | null) {
    if (!control) {
      return;
    }
    let value = control.getRawValue().quantity;
    if (value >= 0) {
      value = value.toString().replace(/[^0-9.]/g, '');
      if (parseFloat(value) > this.MAX_QUANTITY) {
        value = this.MAX_QUANTITY.toString()
      };
      const qnt = control.get('quantity');
      if (qnt) {
        qnt.setValue(value, {emitEvent: false});
      }
    }
  }

  therapyTypeValidator(formGroup: UntypedFormGroup) {
    const therapyType = formGroup.get("therapyType")?.value;
    const variableAssumptions = formGroup.get("variableAssumptions")?.value;

    if (
      therapyType === "variabile" &&
      (!Array.isArray(variableAssumptions) || variableAssumptions.length === 0)
    ) {
      return {emptyVariableAssumptions: true};
    }

    return null;
  }

  private initFieldsInsertDefaults() {
    if (this.insulinTherapy) {
      this.form.patchValue({assumptionsUnit: "unit_of_insulin"}, {emitEvent: false});
    }

    // If in "insert" mode and there are some initial default values -> apply them to form
    if (this.mode === "insert" && this.insertDefaults) {
      // Set drug
      if (this.insertDefaults.drug) {
        this.form.patchValue({drug: {...this.insertDefaults.drug}}, {emitEvent: false});
      }

      // Set assumption unit
      if (this.insertDefaults.assumptionsUnit) {
        this.form.patchValue(
          {assumptionsUnit: this.insertDefaults.assumptionsUnit},
          {emitEvent: false},
        );
      }

      // Set regular frequency
      if (this.insertDefaults.regular?.frequency) {
        this.form.patchValue(
          {regular: {frequency: {...this.insertDefaults.regular?.frequency}}},
          {emitEvent: false},
        );
      }

      if (this.insertDefaults.atLeastOneAssumptionNeeded) {
        // Apply validator
        this.assumptionsControls.setValidators((control) => {
          return Array.isArray(control.value) && control.value.length > 0
            ? null
            : {emptyAssumptionsList: true};
        });
      }

      if (this.insertDefaults.therapyDuration) {
        if (this.insertDefaults.therapyDuration != null) {
          if (this.insertDefaults.therapyDuration > 0) {
            this.form.patchValue(
              {
                neverEnds: false,
                duration: this.insertDefaults.therapyDuration,
                endDate: format(addDays(this.today, this.insertDefaults.therapyDuration), DATE_FMT),
              },
              {emitEvent: false},
            );
          } else {
            this.form.patchValue(
              {
                neverEnds: true,
                endDate: format(new Date(this.MAXIMUM_DATE_UNIX), DATE_FMT),
              },
              {emitEvent: false},
            );
          }
        }
      }
    }

    /*if (this.mode === "edit") {
     if(this.variableDailyAssumptionsControls.length > 0){
               this.form.patchValue({
            regular: {
              frequency: { type: "at_need" as TrecFreqType, value: null }, // TrecFreqType - (number | number[] | null)
              assumptions: [],
            },
          });
          this.setupRegularAssumptionControl([]);
     }
    }*/
  }

  protected resetDefaultValues() {
    this.form.patchValue({
      drug: null,
      assumptionsUnit: null,
      startDate: format(this.today, DATE_FMT), // Date
      neverEnds: this.DEFAULT_NEVER_ENDS,
      duration: this.DEFAULT_DURATION,
      endDate: this.DEFAULT_NEVER_ENDS
        ? format(new Date(this.MAXIMUM_DATE_UNIX), DATE_FMT)
        : format(addDays(this.today, this.DEFAULT_DURATION), DATE_FMT), // Date

      regular: {
        frequency: {type: "at_need" as TrecFreqType, value: null}, // TrecFreqType - (number | number[] | null)
        assumptions: [],
      },

      variable: {
        dailyAssumptions: [],
      },

      notes: "",
    } as FormTherapyData);

    this.initFieldsInsertDefaults();

    this.cycleInfo = null;

    if (this.isInsertMode) {
      // Apply template if any
      this.applyTemplate(this.therapyTemplate);
    }
  }

  // Callbacks ////////////////////////////////////////////////////////////////////////////////////

  async onOpenOncoTherapyForm() {
    // Get current modal
    const currModal = await this.modalCtrl.getTop();
    // Dismiss old one and ask for special form
    await currModal?.dismiss({openInsertOncoTherapy: true});
  }

  onDrugSearch(searchValue: string) {
    if (searchValue && typeof searchValue === "string") {
      this.drugSearchValue$.next({
        name: searchValue,
        box: "",
        form: "",
        // If searching for an insulin therapy -> filter by active ingredient
        active_ingredient: this.insulinTherapy ? "insulin" : "",
        _id: "",
      });
    }
  }

  onAddRegularAssumption(assumptionValue?: Partial<TrecTherapyAssumptionData>) {
    // If frequency is 'at_need' allow only a single assumption
    const freqType = this.formValue?.regular?.frequency?.type;
    if (freqType === "at_need") {
      const existing = this.assumptionsControls ? this.assumptionsControls.length : 0;
      if (existing >= 1) {
        this.c3SnackbarStatus.showInfo({
          message: "Per la modalità 'al bisogno' è consentita una sola assunzione",
        });
        return;
      }
    }

    // Ensure assumptions unit as been selected

    const assumptionsUnit = this.form.getRawValue().assumptionsUnit;

    if (assumptionsUnit) {
      this.assumptionsControls.push(
        this.fb.group({
          _id: [assumptionValue?._id ?? generateUuid(), Validators.required],
          quantity: [
            this.toFixedTwoDecimalPlaces(assumptionValue?.quantity ?? 1),
            [
              Validators.required,
              Validators.min(0.25),
              Validators.max(this.MAX_QUANTITY),
              //Validators.pattern(/^[0-50](\.(00|25|50|75))?$/)
            ]
          ],
          unit: [assumptionValue?.unit ?? assumptionsUnit, Validators.required],
          moment: [
            assumptionValue?.moment ?? undefined,
            this.isDiabetesContext ? Validators.required : [],
          ], // 'at_need' as TrecMoment
          time: [
            this.formValue.regular.frequency?.type === "at_need"
              ? "00:00"
              : (assumptionValue?.time ?? "08:00"),
            Validators.required,
          ],
        } as {[key in keyof TrecTherapyAssumptionData]: any}),
      );

      this.assumptionsControls.markAsDirty();
    } else {
      this.c3SnackbarStatus.showInfo({
        message: "Prima è necessario definire la modalità di assunzione del farmaco",
      });
    }
  }

  onRemoveRegularAssumption(index: number) {
    this.assumptionsControls.removeAt(index);
    this.assumptionsControls.markAsDirty();
  }

  prepareData(): TrecTherapy | null {
    // Convert form into therapy
    const isEditMode = this.mode === "edit";

    if (isEditMode && !this.item) {
      throw new Error(`Trying to submit editing of a therapy but therapy data are unknown`);
    }

    const startDateControlWasDisabled = this.startDateControl.disabled;
    this.startDateControl.enable();
    const startDate = startOfDay(new Date(this.formValue.startDate!)); // eslint-disable-line @typescript-eslint/no-non-null-assertion
    if (startDateControlWasDisabled) {
      this.startDateControl.disable();
    }

    let therapy = TrecTherapy.create(
      {
        id: isEditMode ? this.item!.id : undefined, // eslint-disable-line @typescript-eslint/no-non-null-assertion
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        patientId: isEditMode
          ? this.item!.patientId || ""
          : this.store.selectSnapshot(CommonState.patientId) || "",
      },
      {
        start_date: startDate.valueOf(),
        end_date: endOfDay(new Date(this.formValue.endDate!)).valueOf(), // eslint-disable-line @typescript-eslint/no-non-null-assertion
        source: isEditMode ? this.item!.metadata.source : environment.appId, // eslint-disable-line @typescript-eslint/no-non-null-assertion
        provenance: isEditMode ? this.item!.metadata.provenance : "medical", // eslint-disable-line @typescript-eslint/no-non-null-assertion
        device: isEditMode ? this.item!.metadata.device : undefined, // eslint-disable-line @typescript-eslint/no-non-null-assertion
        entry_date: new Date().valueOf(),
        doctor: {
          id: this.store.selectSnapshot(CommonState.userId)!,
          name:
            this.store.selectSnapshot(CommonState.loggedUserData)?.personalData?.surname! +
            " " +
            this.store.selectSnapshot(CommonState.loggedUserData)?.personalData?.name,
        },
        structure: this.store.selectSnapshot(CommonState.currentStructureId)!,
      },
      {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        drug: this.formValue.drug ?? this.item?.data.drug!,
        state: "active",
        options: [],

        regular:
          this.therapyType === "regular"
            ? // User has filled the "regular" part of the therapy -> copy it
            {
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              assumptions:
                this.formValue.regular.assumptions?.map((a) => ({
                  ...a,
                  moment: a.moment ?? undefined,
                })) ?? [],
              frequency: {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                type: this.formValue.regular.frequency!.type,
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                values: this.formValue.regular.frequency!.value
                  ? typeof this.formValue.regular.frequency?.value === "number"
                    ? [this.formValue.regular.frequency.value as number]
                    : // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    (this.formValue.regular.frequency!.value as number[])
                  : [],
              },
            }
            : // "regular" part of therapy should be discarded -> fill with default values
            {frequency: {type: "at_need", values: []}, assumptions: []},

        variable:
          this.therapyType === "variable"
            ? // User has filled the "variable" part of the therapy -> copy it
            {
              assumptions: (this.formValue.variable.dailyAssumptions ?? []).map(
                (dailyA) =>
                  dailyA.assumptions?.map((a) => ({...a, moment: a.moment ?? undefined})) ?? [],
              ),
            }
            : // "variable" part of therapy should be discarded -> fill with default values
            {assumptions: []},

        notes: this.formValue.notes ?? "",
        extra: {
          cycle_duration: this.cycleInfo?.duration,
          cycle_repeat: this.cycleInfo?.repeat,
          cycle_view_start_padding: this.cycleInfo?.startPadding ?? undefined,
        },
      },
    );

    // Add extra useful tags
    if (this.therapyTemplate) {
      therapy = therapy.addTags([...`template:${this.therapyTemplate.id}`]);
    }

    return therapy;
  }

  /**
   * Set the part of therapy that the user needs to fill (regular/variable).
   *
   * @param type Type of therapy to show
   * @param skipConversion Skip conversion of (potentially) already filled regular/variable part into variable/regular.
   */
  setTherapyType(type: "regular" | "variable", skipConversion = false) {
    this.zone.runOutsideAngular(() => {
      const oldType = this.therapyType;

      this.therapyType = type;

      if (this.isViewMode) {
        this.cd.markForCheck();
        return;
      }

      if (skipConversion) {
        this.cd.markForCheck();
        return;
      }

      // Try to convert regular schema into variable and viceversa

      if (oldType === "regular" && type === "variable") {
        // regular -> variable
        // Fill each day in variable assumptions until the end of the therapy

        // Ensure variable data form is not already filled
        if (this.variableDailyAssumptionsControls.controls.length === 0) {
          const convertedVariableAssumptions = this.convertRegularToVariable();

          this.form.patchValue({
            regular: {
              frequency: {type: "at_need" as TrecFreqType, value: null}, // TrecFreqType - (number | number[] | null)
              assumptions: [],
            },
          });
          this.setupRegularAssumptionControl([]);

          this.setupVariableAssumptionControl(convertedVariableAssumptions);
        }
        this.variableDailyAssumptionsControls.setValidators((control) => {
          return Array.isArray(control.value) && control.value.length > 0
            ? null
            : {emptyAssumptionsList: true};
        });
        this.variableDailyAssumptionsControls.updateValueAndValidity();
        this.variableDailyAssumptionsControls.setValidators((control: AbstractControl) =>
          this.validateDailyAssumptions(control as UntypedFormArray),
        );
        this.variableDailyAssumptionsControls.updateValueAndValidity();
      }

      if (oldType === "variable" && type === "regular") {
        // variable -> regular
        // Ensure regular data form is not already filled
        if (
          (this.form.get("regular")?.get("assumptions") as UntypedFormArray).value?.length === 0
        ) {
          // Clear all
          this.setupVariableAssumptionControl([]);
        }
        this.assumptionsControls.setValidators((control) => {
          return Array.isArray(control.value) && control.value.length > 0
            ? null
            : {emptyAssumptionsList: true};
        });
      }

      this.cd.markForCheck();
    });
  }

  validateDailyAssumptions(formArray: UntypedFormArray): ValidationErrors | null {
    let isValid = formArray.controls.some((day) => {
      // Assumi che `day` sia un FormGroup con un campo `assumptions` che è un FormArray
      const assumptions = day.get("assumptions") as UntypedFormArray;
      return assumptions && assumptions.length > 0;
    });

    return isValid ? null : {emptyDailyAssumptions: true};
  }

  showAssumptionUnitSelectedAlert() {
    this.c3SnackbarStatus.showInfo({
      message:
        "La forma farmaceutica è cambiata. Si consiglia di verificare nuovamente i dosaggi impostati",
    });
  }

  onRecalcCycleDuration(duration: number, repeat: number) {
    this.zone.runOutsideAngular(() => {
      const tDuration = duration * repeat;

      if (tDuration <= 0) {
        return;
      }

      // 1. Update therapy end date and total duration
      this.durationControl.patchValue(tDuration);

      // 2. Update cycle info
      this.cycleInfo = {...this.cycleInfo, duration, repeat};

      // 3. Update variable assumptions schema
      if (this.therapyTemplate) {
        this.applyVariableAssumptionsFromTemplate(this.therapyTemplate, this.cycleInfo, tDuration);
      }

      this.cd.markForCheck();
    });
  }

  update() {
    this.zone.runOutsideAngular(() => {
      super.update();

      this.resetDefaultValues();

      // Reset assumptions controls
      (this.form.get("regular") as UntypedFormGroup).setControl("assumptions", this.fb.array([]));
      (this.form.get("variable") as UntypedFormGroup).setControl(
        "dailyAssumptions",
        this.fb.array([]),
      );

      if (this.item) {
        const {data, metadata} = this.item;

        // Prepare regular assumptions controls
        this.setupRegularAssumptionControl(data.regular.assumptions);

        // Prepare variable assumptions controls
        this.setupVariableAssumptionControl(data.variable.assumptions);

        // Set new values
        this.form.patchValue({
          drug: {
            name: data.drug.name,
            _id: data.drug._id,
            active_ingredient: data.drug.active_ingredient,
            box: data.drug.box,
            form: data.drug.form,
          },
          assumptionsUnit: data.AI
            ? this.setAssumptionUnit(data.AI, data)
            : (this.item.getAssumptionUnit() ?? null),
          startDate: format(metadata.start_date, DATE_FMT),
          neverEnds: metadata.end_date >= this.MAXIMUM_DATE_UNIX,
          duration: differenceInDays(metadata.end_date, metadata.start_date),
          endDate: format(new Date(metadata.end_date), DATE_FMT),

          regular: {
            frequency: {
              type: data.regular.frequency.type,
              value: data.regular.frequency.values,
            },
            assumptions: data.regular.assumptions,
          },

          variable: {
            dailyAssumptions: data.variable.assumptions.map((dailyAssumptions) => ({
              assumptions: dailyAssumptions,
            })),
          },

          notes: data.notes || "",
        } as FormTherapyDataKeys);

        // Collect extra info about therapy
        this.cycleInfo =
          this.item.data?.extra?.cycle_duration && this.item.data.extra?.cycle_repeat
            ? {
              duration: this.item.data.extra.cycle_duration ?? 0,
              repeat: this.item.data.extra.cycle_repeat ?? 0,
              startPadding:
                (this.item.data.extra.cycle_view_start_padding as number | undefined) ??
                undefined,
            }
            : null;

        if (this.variableDailyAssumptionsControls.length === 0) {
          // Apply validator
          this.assumptionsControls.setValidators((control) => {
            return Array.isArray(control.value) && control.value.length > 0
              ? null
              : {emptyAssumptionsList: true};
          });
        }
      } else {
        // Set default values
        this.form.patchValue({
          drug: null,
          assumptionsUnit: null,
          startDate: format(this.today, DATE_FMT),
          neverEnds: this.DEFAULT_NEVER_ENDS,
          duration: this.DEFAULT_DURATION,
          endDate: this.DEFAULT_NEVER_ENDS
            ? format(new Date(this.MAXIMUM_DATE_UNIX), DATE_FMT)
            : format(addDays(this.today, this.DEFAULT_DURATION), DATE_FMT),

          regular: {
            frequency: {
              type: (this.context === RegistryContexts.GESTATIONAL
                ? "every_x_days"
                : "at_need") as TrecFreqType,
              value: this.context === RegistryContexts.GESTATIONAL ? 1 : null,
            },
            assumptions: [],
          },

          variable: {
            dailyAssumptions: [],
          },

          notes: "",
        } as FormTherapyDataKeys);

        this.initFieldsInsertDefaults();
      }

      // If therapy has variable assumptions -> show variable section
      if (
        (this.form.get("variable") as UntypedFormGroup).get("dailyAssumptions")?.value.length > 0
      ) {
        this.setTherapyType("variable", true);
      }

      // Choose the form to see
      this.updateSpecialFormType();

      // Re-update form enable/disable in order to propagate state also in new assumptions controls

      if (this.isViewMode) {
        this.assumptionsControls.disable();
        this.variableDailyAssumptionsControls.disable();
      } else {
        this.assumptionsControls.enable();
        this.variableDailyAssumptionsControls.enable();
      }

      // Special cases
      if (this.isEditMode) {
        // Drug cannot be changed
        this.drugControl.disable();

        // Start date should not be changed and should be now... Why? Otherwise, if (in the future but before the new start date) the
        // therapy is suspended it can happen that the therapy restarts from a start date which is before the one prescribed by the doctor
        // const oldStartDate = this.startDateControl.value;
        this.startDateControl.patchValue(format(new Date(), DATE_FMT));
        // this.startDateControl.disable();

        const diffDays = differenceInDays(
          new Date(this.startDateControl.value),
          new Date(this.item!.metadata.start_date),
        );
        this.sliceVariableAssumptions(diffDays);

        // Now cycle schema in table is shifted -> Save difference between days to un-shift the schema
        this.cycleInfo = this.cycleInfo
          ? {
            ...this.cycleInfo,
            startPadding: Math.max(
              ((this.item!.data.extra?.cycle_view_start_padding as number | undefined) ?? 0) +
              diffDays,
              0,
            ),
          }
          : null;
      }

      if (this.isInsertMode) {
        // Apply template if any
        this.applyTemplate(this.therapyTemplate);
      }

      if (this.AIFlag) {
        //console.log("AIFlag attivo - controllo campi form");
        // 👇 Controllo dei campi valorizzati
        const allFieldsFilled =
          this.form.get("drug")?.value &&
          this.form.get("assumptionsUnit")?.value &&
          this.form.get("startDate")?.value &&
          //this.form.get('neverEnds')?.value &&
          this.form.get("duration")?.value &&
          this.form.get("endDate")?.value &&
          this.form.get("regular.frequency")?.value.type !== null &&
          this.form.get("regular.frequency")?.value.value !== null &&
          this.form.get("regular.assumptions")?.value?.length > 0; // se executions è array

        if (allFieldsFilled) {
          // Impostiamo il form come valido e con pristine = false (dirty) perché è stato
          // auto-popolato dall'AI ma vogliamo segnalarlo come modificato.
          this.clearFormErrorsAndMarkDirty(this.form as UntypedFormGroup);
          // assicuriamoci che il form sia aggiornato e coerente
          this.form.updateValueAndValidity();
          //console.log('👉 Form marcata come dirty e valida');
        } else {
          //console.log('⚠️ Mancano alcuni campi');
        }
      }

      this.cd.markForCheck();
    });
  }

  private updateSpecialFormType() {
    const currentDrug: Drug | null = this.drugControl.value;

    // If drug is capecitabine-based -> use special form for insert
    if (ONCO_DRUGS_IDS.includes(currentDrug?._id ?? "") && this.isInsertMode) {
      // Onco therapies
      this.specialFormType = "capecitabine";
      this.assumptionsUnitControl.disable();
    } else if (currentDrug?.active_ingredient.toLowerCase().includes("insulin")) {
      // Insuling therapies
      this.specialFormType = "generic";
      this.form.patchValue({assumptionsUnit: "unit_of_insulin"}, {emitEvent: false});
      this.assumptionsUnitControl.disable();
    } else {
      // Generic therapies
      this.specialFormType = "generic";

      if (!this.isViewMode) {
        this.assumptionsUnitControl.enable();
      }
    }

    this.cd.markForCheck();
  }

  private setupRegularAssumptionControl(
    assumptions: Partial<TrecTherapyAssumptionData>[] = [],
  ): void {
    (this.form.get("regular") as UntypedFormGroup).setControl(
      "assumptions",
      this.fb.array(
        assumptions.map((a) =>
          this.fb.group({
            _id: [a._id ?? generateUuid(), Validators.required],
            quantity: [
              this.toFixedTwoDecimalPlaces(a.quantity),
              [
                Validators.required,
                Validators.min(0.25),
                Validators.max(this.MAX_QUANTITY),
                //Validators.pattern(/^[0-50](\.(00|25|50|75))?$/)
              ]
            ],
            unit: [a.unit, Validators.required],
            moment: [a.moment, this.isDiabetesContext ? Validators.required : []],
            time: [a.time, Validators.required],
          }),
        ),
      ),
    );
  }

  private setupVariableAssumptionControl(
    assumptions: (Partial<TrecTherapyAssumptionData>[] | null)[] = [],
  ): void {
    (this.form.get("variable") as UntypedFormGroup).setControl(
      "dailyAssumptions",
      this.fb.array(
        assumptions.map((dailyAssumptions) =>
          this.fb.group({
            assumptions: this.fb.array(
              (dailyAssumptions ?? []).map((a) =>
                this.fb.group({
                  _id: [a._id ?? generateUuid(), Validators.required],
                  quantity: [
                    this.toFixedTwoDecimalPlaces(a.quantity),
                    [
                      Validators.required,
                      Validators.min(0.25),
                      Validators.max(this.MAX_QUANTITY),
                      //Validators.pattern(/^[0-50](\.(00|25|50|75))?$/)
                    ]
                  ],
                  unit: [a.unit, Validators.required],
                  time: [a.time, Validators.required],
                  moment: [a.moment, []],
                }),
              ) ?? [],
            ),
          }),
        ),
      ),
    );
  }

  /**
   * Remove part of the current variable assumption's days.
   *
   * @param start Start index of the new variable assumption schema
   * @param end End index of the new variable assumption schema
   */
  private sliceVariableAssumptions(start?: number, end?: number) {
    const variableDailyAssumptions = (this.form.get("variable") as UntypedFormGroup).get(
      "dailyAssumptions",
    ) as UntypedFormArray;
    if (variableDailyAssumptions?.value.length > 0) {
      const st = Math.max(start ?? 0, 0);
      variableDailyAssumptions.controls = variableDailyAssumptions.controls.slice(st, end);
    }
  }

  private applyTemplate(tt: TherapyTemplate | null): void {
    if (!tt) {
      return;
    }

    if (tt.drug._id && tt.drug.name && tt.drug.box && tt.drug.form && tt.drug.active_ingredient) {
      this.form.patchValue({
        drug: {...tt.drug},
      });
    } else {
      // Trigger search and autocomplete functionality
      this.drugSelector.setSearchValue(tt.drug.active_ingredient);
    }

    if (tt.duration) {
      this.form.patchValue({
        neverEnds: false,
        // NB: set "neverEnds" triggers the auto-reset of duration and endDate fields.
        // So, here, setting duration and endDate wouldn't be needed.
        duration: tt.duration,
        endDate: format(addDays(new Date(this.startDateControl.value), tt.duration ?? 0), DATE_FMT),
      });
    }

    // Set assumptionUnit if available
    this.form.patchValue({
      assumptionsUnit: this.findAssumptionUnit(tt),
    });

    if (tt.regular) {
      this.form.patchValue({
        regular: {
          frequency: {
            type: tt.regular.frequency.type,
            value: tt.regular.frequency.values,
          },
          // assumptions: tt.regular.assumptions ?? [],
        },
      });

      // Add asumption controls
      this.setupRegularAssumptionControl(
        tt.regular.assumptions.map<Partial<TrecTherapyAssumptionData>>((a) => ({
          _id: generateUuid(),
          quantity: a.quantity,
          unit: a.unit as TrecAssumptionUnit,
          time: a.time,
        })),
      );
    }

    if (tt.variable) {
      this.applyVariableAssumptionsFromTemplate(tt);
    }

    // Set regular/variable therapy toggle depending on template
    this.setTherapyType(tt.variable ? "variable" : "regular", true);

    if (tt.notes) {
      this.form.patchValue({
        notes: tt.notes,
      });
    }

    // Collect extra info
    this.cycleInfo =
      tt.extra?.cycle_duration && tt.extra?.cycle_repeat
        ? {
          duration: tt.extra.cycle_duration as number,
          repeat: tt.extra.cycle_repeat as number,
        }
        : null;

    this.form.updateValueAndValidity();
  }

  private applyVariableAssumptionsFromTemplate(
    tt: TherapyTemplate,
    overrideCycleInfo?: TherapyCycleInfo,
    overrideTherapyDuration?: number,
  ) {
    if (tt.variable) {
      let normalizedAssumptions = tt.variable.assumptions.map((dailyAssumptions) =>
        dailyAssumptions.map<Partial<TrecTherapyAssumptionData>>((a) => ({
          _id: generateUuid(),
          quantity: a.quantity,
          unit: a.unit as TrecAssumptionUnit,
          time: a.time,
          moment: (a as any).moment,
        })),
      );

      // Collect info
      const therapyDuration = overrideTherapyDuration ?? tt.duration;
      const cycleInfo =
        overrideCycleInfo ??
        (tt.extra?.cycle_duration && tt.extra?.cycle_repeat
          ? {duration: tt.extra.cycle_duration, repeat: tt.extra.cycle_repeat}
          : null);

      if (cycleInfo) {
        // Schema is cyclic
        // Unwrap cyclic scheme filling all the variable schema days

        let newSchema = normalizedAssumptions;
        for (let i = 0; i < cycleInfo.repeat; i++) {
          newSchema = [...newSchema, ...normalizedAssumptions];
        }
        newSchema = newSchema.slice(0, therapyDuration);
        // Recreate ids
        newSchema = newSchema.map((dailyAssumptions) =>
          dailyAssumptions.map<Partial<TrecTherapyAssumptionData>>((a) => ({
            _id: generateUuid(),
            ...a,
          })),
        );

        normalizedAssumptions = newSchema;
      }

      // Add variable asumption controls
      this.setupVariableAssumptionControl(normalizedAssumptions);
    }
  }

  private findAssumptionUnit(tt: TherapyTemplate): TrecAssumptionUnit | null {
    return (
      (tt.regular?.assumptions?.[0]?.unit as TrecAssumptionUnit | undefined) ??
      (tt.variable?.assumptions?.find((dA) => dA?.find((a) => a.unit))?.find((a) => a.unit)
        ?.unit as TrecAssumptionUnit | undefined) ??
      null
    );
  }

  private convertRegularToVariable(): (Partial<TrecTherapyAssumptionData>[] | null)[] {
    const duration = this.durationControl.value;
    const neverEnds = this.neverEndsControl.value;
    const regular: Partial<FormTherapyDataRegular> | null = this.form.get("regular")?.value;

    if (!duration) {
      return [];
    }
    if (neverEnds) {
      return [];
    }

    switch (regular?.frequency?.type) {
      case "at_need":
        return [];

      case "every_x_days":
        const varAssumptionsD: (Partial<TrecTherapyAssumptionData>[] | null)[] = [];
        const daysNumberD: number | null = Array.isArray(regular?.frequency?.value)
          ? regular?.frequency?.value[0]
          : regular?.frequency?.value;

        if (daysNumberD) {
          for (let i = 0; i < duration; i++) {
            const dailyAD: Partial<TrecTherapyAssumptionData>[] =
              i % daysNumberD === 0 ? (regular.assumptions ?? []) : [];
            varAssumptionsD.push(dailyAD);
          }
        }

        return varAssumptionsD;

      case "day_of_week":
        const varAssumptionsW: (Partial<TrecTherapyAssumptionData>[] | null)[] = [];
        const daysNumberW: number[] = Array.isArray(regular?.frequency?.value)
          ? regular?.frequency?.value
          : [];

        for (let i = 0; i < duration; i++) {
          const dailyAW: Partial<TrecTherapyAssumptionData>[] = daysNumberW.includes((i + 1) % 7)
            ? (regular.assumptions ?? [])
            : [];
          varAssumptionsW.push(dailyAW);
        }

        return varAssumptionsW;

      case "day_of_month":
        const varAssumptionsM: (Partial<TrecTherapyAssumptionData>[] | null)[] = [];
        const daysNumberM: number[] = Array.isArray(regular?.frequency?.value)
          ? regular?.frequency?.value
          : [];

        for (let i = 0; i < duration; i++) {
          const dailyAM: Partial<TrecTherapyAssumptionData>[] = daysNumberM.includes((i + 1) % 31)
            ? (regular.assumptions ?? [])
            : [];
          varAssumptionsM.push(dailyAM);
        }

        return varAssumptionsM;

      default:
        return [];
    }
  }

  private setAssumptionUnit(AI: boolean | undefined, data: any): TrecAssumptionUnit | null {
    if (!AI) {
      return null;
    }

    if (data?.regular?.assumptions && data.regular.assumptions.length > 0) {
      return data.regular.assumptions[0].unit;
    } else if (data?.variable?.assumptions && data.variable.assumptions.length > 0) {
      return data.variable.assumptions[0].unit;
    } else {
      return null;
    }
  }

  private updateAssumptionUnits() {
    const all = TrecTherapy.TRANSLATION[this.localeId]["regular.assumptions.unit"];

    if (this.context?.includes(RegistryContexts.CARDIO_DIABETES)) {
      this.ASSUMPTION_UNITS = {
        patch: all["patch"],
        spoon: all["spoon"],
        tablet: all["tablet"],
        drop: all["drop"],
        small_vial: all["small_vial"],
        puff: all["puff"],
        suppository: all["suppository"],
        liter: all["liter"],
      };
    } else {
      this.ASSUMPTION_UNITS = all;
    }
  }

  getClassOf(val: any) {
    {
      switch (val) {
        case "prescritta":
          return "green";
        case "modificata":
          return "yellow";
        case "cancellata":
          return "red";
        case "sospesa":
          return "orange";
        case "riattivata":
          return "blue";
      }
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();

    this.formValueSubscrs.unsubscribe();
  }

  /**
   * Clear validation errors and mark form/group/controls as pristine/untouched recursively
   */
  private clearFormErrorsAndMarkDirty(
    group: UntypedFormGroup | UntypedFormArray | UntypedFormControl,
  ) {
    try {
      if ((group as UntypedFormGroup).controls) {
        const fg = group as UntypedFormGroup;
        Object.keys(fg.controls).forEach((key) => {
          const control: any = fg.controls[key];
          if (control.controls) {
            // FormGroup or FormArray
            this.clearFormErrorsAndMarkDirty(control);
          } else {
            control.setErrors(null);
            control.markAsDirty();
            control.markAsTouched();
          }
        });
        fg.setErrors(null);
        fg.markAsDirty();
        fg.markAsTouched();
      } else {
        const fc = group as UntypedFormControl;
        fc.setErrors(null);
        fc.markAsDirty();
        fc.markAsTouched();
      }
    } catch (e) {
      // ignore errors
    }
  }
}
